/**
 * Header/footer localization: currency selector + GTranslate language selector.
 *
 * Extracted from snippets/header-localization.liquid, which renders three times
 * per page (mobile drawer, desktop header, footer) and previously emitted this
 * entire script inline on each render -- roughly 24KB of uncacheable duplicate
 * JS in every HTML response, against Shopify's documented 16KB budget.
 *
 * Behaviour is unchanged. The only structural difference is that the per-instance
 * IIFE (which closed over a Liquid-interpolated container id) now iterates every
 * [id^="header-localization-"] container and gives each its own independent state,
 * so a single cached file drives all three mount points.
 *
 * Loaded once from layout/theme.liquid with defer.
 */
(function () {
  'use strict';

  var CONTAINER_SELECTOR = '[id^="header-localization-"]';

  /* ------------------------------------------------------------------ *
   * GTranslate loader (global, runs once)
   * ------------------------------------------------------------------ */
  function initGTranslate() {
    if (window.gtranslateInitialized) return;
    window.gtranslateInitialized = true;

    window.gtranslateSettings = {
      default_language: 'en',
      languages: ['en', 'fr', 'es'],
      wrapper_selector: '.gtranslate_wrapper',
      select_language_label: ''
    };

    window.gtranslateLoadAttempts = 0;
    window.gtranslateMaxAttempts = 3;

    function loadGTranslateScript() {
      window.gtranslateLoadAttempts++;
      var gtScript = document.createElement('script');
      gtScript.src = 'https://cdn.gtranslate.net/widgets/latest/dropdown.js';
      gtScript.defer = true;

      gtScript.onload = function () {
        window.gtranslateLoaded = true;
      };

      gtScript.onerror = function () {
        console.warn('GTranslate script failed to load, attempt ' + window.gtranslateLoadAttempts);
        if (window.gtranslateLoadAttempts < window.gtranslateMaxAttempts) {
          // Exponential backoff
          setTimeout(loadGTranslateScript, 1000 * window.gtranslateLoadAttempts);
        }
      };

      document.head.appendChild(gtScript);
    }

    loadGTranslateScript();
  }

  /* ------------------------------------------------------------------ *
   * Shared helpers
   * ------------------------------------------------------------------ */

  // Auto-size a select to fit its selected option text
  function autoSizeSelect(select) {
    if (!select || !select.options || select.selectedIndex < 0) return;
    var selectedOption = select.options[select.selectedIndex];
    if (!selectedOption) return;
    var selectedText = selectedOption.text || '';
    if (!selectedText || selectedText === 'Select Language') return;

    var measure = document.createElement('span');
    measure.style.cssText =
      'position:absolute;visibility:hidden;white-space:nowrap;font-family:"Cabinet Grotesk",sans-serif;font-size:1.5rem;font-weight:500;';
    measure.textContent = selectedText;
    document.body.appendChild(measure);
    select.style.width = measure.offsetWidth + 'px';
    document.body.removeChild(measure);
  }

  /* ------------------------------------------------------------------ *
   * Per-container initialisation
   * ------------------------------------------------------------------ */
  function initContainer(container) {
    if (!container || container.hasAttribute('data-localization-initialized')) return;
    container.setAttribute('data-localization-initialized', 'true');

    /* ---- Currency: submit Shopify's localization form (carries CSRF token) ---- */
    var currencySelect = container.querySelector('[data-currency-select]');
    if (currencySelect && !currencySelect.hasAttribute('data-initialized')) {
      currencySelect.setAttribute('data-initialized', 'true');
      autoSizeSelect(currencySelect);

      currencySelect.addEventListener('change', function () {
        var newValue = this.value;

        // Keep the other currency selects visually in sync before reload
        document.querySelectorAll('[data-currency-select]').forEach(function (otherSelect) {
          if (otherSelect !== currencySelect && otherSelect.value !== newValue) {
            otherSelect.value = newValue;
            autoSizeSelect(otherSelect);
          }
        });

        var formId = this.getAttribute('data-form-id');
        var form = document.getElementById(formId);
        if (form) {
          var countryInput = form.querySelector('[data-country-input]');
          if (countryInput) countryInput.value = newValue;
          form.submit();
        }
      });
    }

    /* ---- Language: wait for GTranslate to inject its select, then wire it ---- */
    var gtWrapper = container.querySelector('.gtranslate_wrapper');

    if (!gtWrapper) {
      container.classList.add('is-ready');
      return;
    }

    var isReady = false;
    var retryCount = 0;
    var maxRetries = 50;
    var retryInterval = 100;
    var fallbackTimeout = 5500;

    function setupGtSelect(gtSelect) {
      if (isReady) return true;
      if (!gtSelect || gtSelect.hasAttribute('data-initialized')) return false;
      if (gtSelect.options.length <= 1) return false;

      // Require a real selection, not the "Select Language" placeholder
      var hasValidSelection = false;
      for (var i = 0; i < gtSelect.options.length; i++) {
        if (gtSelect.options[i].selected && gtSelect.options[i].value !== '') {
          hasValidSelection = true;
          break;
        }
      }
      if (!hasValidSelection) return false;

      gtSelect.setAttribute('data-initialized', 'true');
      autoSizeSelect(gtSelect);

      gtSelect.addEventListener('change', function () {
        var newValue = this.value;
        autoSizeSelect(gtSelect);

        // Sync every other GTranslate select across mount points
        document.querySelectorAll('select.gt_selector').forEach(function (otherSelect) {
          if (otherSelect !== gtSelect && otherSelect.value !== newValue) {
            otherSelect.value = newValue;
            autoSizeSelect(otherSelect);
          }
        });

        if (typeof window.doGTranslate === 'function') {
          // Value is either "en|fr" or a bare "fr"
          var targetLang = newValue.indexOf('|') !== -1 ? newValue.split('|')[1] : newValue.replace('en|', '');
          window.doGTranslate('en|' + targetLang);
        }
      });

      container.classList.add('is-ready');
      isReady = true;
      return true;
    }

    function trySetup() {
      // Still waiting on the GTranslate script itself
      if (!window.gtranslateLoaded && window.gtranslateLoadAttempts < window.gtranslateMaxAttempts) {
        retryCount++;
        if (retryCount < maxRetries) setTimeout(trySetup, retryInterval);
        return false;
      }

      if (setupGtSelect(gtWrapper.querySelector('select.gt_selector'))) return true;

      retryCount++;
      if (retryCount < maxRetries) setTimeout(trySetup, retryInterval);
      return false;
    }

    // Slight delay so the loader has a chance to start
    setTimeout(trySetup, 200);

    // Also catch the injection directly
    var observer = new MutationObserver(function (mutations, obs) {
      if (setupGtSelect(gtWrapper.querySelector('select.gt_selector'))) obs.disconnect();
    });
    observer.observe(gtWrapper, { childList: true, subtree: true });

    // Fallback: reveal regardless after the timeout
    setTimeout(function () {
      if (!isReady) {
        container.classList.add('is-ready');
        isReady = true;
        var gtSelect = gtWrapper.querySelector('select.gt_selector');
        if (gtSelect) autoSizeSelect(gtSelect);
      }
    }, fallbackTimeout);
  }

  function initAll() {
    document.querySelectorAll(CONTAINER_SELECTOR).forEach(initContainer);
  }

  /* ---- Re-show and re-size on back/forward navigation ---- */
  function handlePopstate() {
    document.querySelectorAll(CONTAINER_SELECTOR).forEach(function (container) {
      container.classList.add('is-ready');
      var currencySelect = container.querySelector('[data-currency-select]');
      if (currencySelect) autoSizeSelect(currencySelect);
      var gtSelect = container.querySelector('select.gt_selector');
      if (gtSelect) autoSizeSelect(gtSelect);
    });
  }

  window.addEventListener('popstate', handlePopstate);
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) handlePopstate();
  });

  /* ------------------------------------------------------------------ */
  initGTranslate();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
